import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table, { type TableProps } from '@cloudscape-design/components/table';
import { TenantRole, type Member } from '@etiquetachef/types';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { CopyValue } from '@/components/CopyValue';
import { formatDateTime } from '@/lib/format';
import { toast } from '@/lib/notifications';
import { useMembers } from '@/features/members/useMembers';
import { useProfiles } from '@/features/members/useProfiles';
import { removeUserFromTenantCallable } from '@/lib/callables';
import { MemberAddDialog } from '@/features/members/MemberAddDialog';
import { MemberCredentialDialog } from '@/features/members/MemberCredentialDialog';
import { MemberRoleDialog } from '@/features/members/MemberRoleDialog';

type Props = {
  tenantId: string;
  /** País canônico do tenant (`tenant.countryCode`) — trava o telefone no add. */
  tenantCountry: string | null;
};

type RowAction = 'role' | 'credential' | 'remove';

const ROLE_BADGE: Record<TenantRole, 'blue' | 'grey' | 'green'> = {
  OWNER: 'green',
  MANAGER: 'blue',
  STAFF: 'grey',
};

/**
 * Aba de usuários do tenant (admin, Cloudscape). Lista os membros e permite
 * adicionar / editar papel / trocar senha-PIN / remover via callables
 * estendidos para super-admin. Mutação real é server-side; o doc do membro
 * atualiza via snapshot.
 *
 * Origem: sem issue — commit f948c64e4, 2026-06-10 18:39 +0200 (abas
 * Usuários e Responsáveis na página do tenant).
 *
 * Contrato: remover chama `removeUserFromTenant({ tenantId, targetUid })`
 * depois de confirmação; papel e credencial abrem `MemberRoleDialog` e
 * `MemberCredentialDialog`; adicionar abre `MemberAddDialog` com o país do
 * tenant.
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Usuários). Spec:
 * `docs/screens/tenant-detail-admin.md`.
 *
 * Dados: `tenants/{tenantId}/members` (`useMembers`) e, para a coluna
 * Telefone, `profiles/{uid}` de cada membro (`useProfiles`) — o OWNER guarda o
 * número no perfil, o convidado no member; o member tem prioridade.
 *
 * Falhas conhecidas:
 *  - o tratamento de erro procura o código (`'failed-precondition'`) dentro de
 *    `err.message`, mas o `FirebaseError` do SDK de Functions traz em
 *    `message` o texto do servidor (ex.: "O criador do estabelecimento não pode ser removido.") e o código em `err.code`
 *    (`functions/failed-precondition`). A mensagem específica nunca aparece; o operador vê
 *    sempre a genérica (ver #926, achados).
 *  - 2026-10-25 (#1021): o erro da remoção ia só por toast, atrás do overlay do
 *    confirm (que fica aberto no erro). Hoje é `Alert` dentro do confirm.
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) erro da remoção em `Alert` no confirm
 *    (mesmo achado do CRM, #174).
 */
export function MembersTab({ tenantId, tenantCountry }: Props): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const result = useMembers(tenantId);
  // O telefone do OWNER fica em /profiles/{uid} (não no member). Assina os
  // perfis dos membros para usar como fallback na coluna Telefone.
  const memberUids = result.kind === 'READY' ? result.members.map((m) => m.uid) : [];
  const profiles = useProfiles(memberUids);

  const [addOpen, setAddOpen] = useState(false);
  const [roleTarget, setRoleTarget] = useState<Member | null>(null);
  const [credentialTarget, setCredentialTarget] = useState<Member | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [removing, setRemoving] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): erro da remoção, mostrado dentro do confirm.
  const [removeError, setRemoveError] = useState<string | null>(null);

  async function handleRemove(): Promise<void> {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeUserFromTenantCallable({ tenantId, targetUid: removeTarget.uid });
      toast.success(t('adminMembers.toast.removed'));
      setRemoveTarget(null);
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay do confirm, que
      // não fecha no erro (mesmo achado do CRM, #174). Vai para o `Alert` dentro dele.
      setRemoveError(
        code.includes('failed-precondition')
          ? t('adminMembers.errors.cannotRemove')
          : t('adminMembers.errors.removeFailed'),
      );
    } finally {
      setRemoving(false);
    }
  }

  if (result.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (result.kind === 'ERROR') {
    return <Alert type="error">{t('adminMembers.errorLoad')}</Alert>;
  }

  const members = result.members;

  const columns: TableProps.ColumnDefinition<Member>[] = [
    {
      id: 'name',
      header: t('adminMembers.columns.name'),
      cell: (m): React.ReactNode => <Box fontWeight="bold">{m.displayName || '—'}</Box>,
      isRowHeader: true,
    },
    {
      id: 'email',
      header: t('adminMembers.columns.email'),
      cell: (m): React.ReactNode => (m.email ? <CopyValue value={m.email} /> : '—'),
    },
    {
      id: 'phone',
      header: t('adminMembers.columns.phone'),
      cell: (m): React.ReactNode => {
        // Member traz phoneE164 só para convidados; o OWNER tem o número no
        // perfil. Pega o primeiro não-vazio (member tem prioridade).
        const phone =
          [m.phoneE164, profiles[m.uid]?.phoneE164].find((p) => p != null && p.length > 0) ?? '';
        return phone ? <CopyValue value={phone} /> : '—';
      },
    },
    {
      id: 'role',
      header: t('adminMembers.columns.role'),
      cell: (m): React.ReactNode => (
        <Badge color={ROLE_BADGE[m.role]}>{t(`adminMembers.roles.${m.role}`)}</Badge>
      ),
    },
    {
      id: 'joinedAt',
      header: t('adminMembers.columns.joinedAt'),
      cell: (m): React.ReactNode => formatDateTime(m.joinedAt, locale),
    },
    {
      id: 'actions',
      header: t('adminMembers.columns.actions'),
      cell: (m): React.ReactNode => (
        <ButtonDropdown
          expandToViewport
          items={[
            { id: 'role', text: t('adminMembers.actions.editRole') },
            {
              id: 'credential',
              text:
                m.role === TenantRole.STAFF
                  ? t('adminMembers.actions.changePin')
                  : t('adminMembers.actions.changePassword'),
            },
            { id: 'remove', text: t('adminMembers.actions.remove') },
          ]}
          onItemClick={({ detail }): void => {
            const action = detail.id as RowAction;
            if (action === 'role') setRoleTarget(m);
            else if (action === 'credential') setCredentialTarget(m);
            else {
              setRemoveError(null);
              setRemoveTarget(m);
            }
          }}
        >
          {t('adminMembers.columns.actions')}
        </ButtonDropdown>
      ),
    },
  ];

  return (
    <SpaceBetween size="m">
      <Header
        variant="h2"
        description={t('adminMembers.description')}
        actions={
          <Button
            variant="primary"
            onClick={(): void => {
              setAddOpen(true);
            }}
          >
            {t('adminMembers.actions.add')}
          </Button>
        }
      >
        {t('adminMembers.title')}
      </Header>

      <Table<Member>
        items={members}
        trackBy="uid"
        variant="container"
        columnDefinitions={columns}
        empty={
          <Box textAlign="center" color="inherit">
            {t('adminMembers.empty')}
          </Box>
        }
      />

      <MemberAddDialog
        open={addOpen}
        tenantId={tenantId}
        tenantCountry={tenantCountry}
        onClose={(): void => {
          setAddOpen(false);
        }}
      />
      <MemberRoleDialog
        tenantId={tenantId}
        member={roleTarget}
        onClose={(): void => {
          setRoleTarget(null);
        }}
      />
      <MemberCredentialDialog
        tenantId={tenantId}
        member={credentialTarget}
        onClose={(): void => {
          setCredentialTarget(null);
        }}
      />
      <AdminConfirm
        visible={removeTarget !== null}
        title={t('adminMembers.removeDialog.title')}
        confirmLabel={t('adminMembers.removeDialog.confirm')}
        cancelLabel={t('adminMembers.addDialog.cancel')}
        loading={removing}
        onConfirm={(): void => {
          void handleRemove();
        }}
        onDismiss={(): void => {
          if (!removing) setRemoveTarget(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">
            {t('adminMembers.removeDialog.body', { name: removeTarget?.displayName ?? '' })}
          </Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {removeError !== null && <Alert type="error">{removeError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </SpaceBetween>
  );
}
